import { DocumentSignatureStatus, DocumentType } from '@bulthaup/api/db';
import { getSignerFullName } from '@bulthaup/api/entities/customer';
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconHistory, IconRefresh } from '@tabler/icons-react';

import { DocumentHistoryModal } from '../-document-history-modal';
import { SignatureRequestButton } from '../../../-components/-signature-request-button';
import { SignatureRequestStatusBadge } from '../../../-components/-signature-request-status-badge';
import { CancelSignatureRequestButton } from '../../../-components/cancel-signature-request-button';
import { TechnicalPlanButton } from '../../../-components/technical-plan/-technical-plan-button';
import { RouterOutput } from '../../../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../../../services/upload';
import { TechnicalPlanTableSection } from './technical-plan-table-section';

export function TechnicalPlanSection({
  project,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
}) {
  const technicalPlanDocument = project.documents.find(
    document => document.type === DocumentType.TECHNICAL_PLAN,
  );

  const oldDocuments = project.documents
    .filter(document => document.type === DocumentType.TECHNICAL_PLAN)
    .slice(1);

  if (!technicalPlanDocument) {
    return (
      <Stack>
        <Text c="dimmed" size="sm" truncate style={{ flex: 1 }}>
          Tableau du plan technique
        </Text>
        <Group align="center">
          <Group gap="xs">
            <TechnicalPlanTableSection project={project} />
          </Group>
        </Group>
        <Group>
          <TechnicalPlanButton
            project={project}
            label="Ajouter un plan technique"
          />
        </Group>
      </Stack>
    );
  }

  if (!technicalPlanDocument.signatureRequestId) {
    const signatureRecipient = getSignerFullName(project.customer);

    return (
      <Stack>
        <Title order={4} size="sm">
          Tableau du plan technique
        </Title>
        <Group>
          <TechnicalPlanTableSection project={project} />
        </Group>

        <Title order={4} size="sm" mt="md">
          Plan technique
        </Title>
        <Text c="dimmed" size="sm" truncate style={{ flex: 1 }}>
          {technicalPlanDocument.fileName}
        </Text>
        <Group align="center">
          <Button
            leftSection={<IconEye size={16} />}
            variant="outline"
            onClick={() => {
              window.open(
                getUTDocumentUrl(technicalPlanDocument.utFileKey),
                '_blank',
              );
            }}
          >
            Plan technique
          </Button>
          <TechnicalPlanButton
            icon={<IconRefresh size={16} />}
            project={project}
            label="Remplacer le plan technique"
          />
        </Group>

        <Group>
          <Text c="dimmed" size="sm">
            Version : {technicalPlanDocument.version}
          </Text>
          {oldDocuments.length > 0 && (
            <Button
              leftSection={<IconHistory size={16} />}
              variant="subtle"
              size="xs"
              onClick={() => {
                modals.open({
                  title: 'Historique des plans techniques',
                  size: 'lg',
                  children: <DocumentHistoryModal documents={oldDocuments} />,
                });
              }}
            >
              Anciennes versions
            </Button>
          )}
        </Group>

        <Group>
          <SignatureRequestButton
            documentId={technicalPlanDocument.id}
            recipient={signatureRecipient}
            label="Envoyer plan technique pour signature"
            signatureRequestName={`Plan technique ${project.name}`}
          />
        </Group>
      </Stack>
    );
  }

  return (
    <Stack>
      <Title order={4} size="sm">
        Tableau du plan technique
      </Title>
      <Group>
        <TechnicalPlanTableSection project={project} />
      </Group>

      <Title order={4} size="sm" mt="md">
        Plan technique
      </Title>
      <Text c="dimmed" size="sm" truncate style={{ flex: 1 }}>
        {technicalPlanDocument.fileName}
      </Text>
      <Group align="center">
        <Button
          leftSection={<IconEye size={16} />}
          variant="outline"
          onClick={() => {
            window.open(
              getUTDocumentUrl(technicalPlanDocument.utFileKey),
              '_blank',
            );
          }}
        >
          Plan technique
        </Button>
        <>
          {technicalPlanDocument.signatureStatus !==
            DocumentSignatureStatus.ONGOING && (
            <>
              <TechnicalPlanButton
                icon={<IconRefresh size={16} />}
                project={project}
                label="Remplacer le plan technique signé"
              />
            </>
          )}
          {technicalPlanDocument.signatureStatus ===
            DocumentSignatureStatus.ONGOING && (
            <CancelSignatureRequestButton
              documentId={technicalPlanDocument.id}
              label="Annuler la signature"
            />
          )}
        </>
      </Group>

      <SignatureRequestStatusBadge
        status={technicalPlanDocument.signatureStatus}
      />

      <Group>
        <Button
          leftSection={<IconHistory size={16} />}
          variant="subtle"
          size="xs"
          onClick={() => {
            modals.open({
              title: 'Historique des plans techniques',
              size: 'md',
              children: <DocumentHistoryModal documents={oldDocuments} />,
            });
          }}
        >
          Voir l'historique
        </Button>
      </Group>
    </Stack>
  );
}
